import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Order } from '../types';
import { ordersAPI, giftCodesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import toast from 'react-hot-toast';

const Payment: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();

  const [order, setOrder] = useState<Order | null>(null);
  const [paymentType, setPaymentType] = useState<'nakit' | 'kart'>('nakit');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Para üstü hesaplama
  const [customerPaid, setCustomerPaid] = useState<string>('');

  // Hediye kodu state'leri
  const [giftCode, setGiftCode] = useState('');
  const [appliedGiftCode, setAppliedGiftCode] = useState<any>(null);
  const [giftCodeDiscount, setGiftCodeDiscount] = useState(0);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  // SumUp kart ödeme state'leri
  const [isWaitingForCardPayment, setIsWaitingForCardPayment] = useState(false);
  const [sumupCheckoutId, setSumupCheckoutId] = useState<string | null>(null);
  const [isWaitingForPosResponse, setIsWaitingForPosResponse] = useState(false);

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId || isNaN(parseInt(orderId))) {
      navigate('/');
      return;
    }

    // Ödeme izni kontrolü
    if (!user?.can_process_payments && user?.role !== 'admin') {
      toast.error('Ödeme alma yetkiniz bulunmuyor');
      navigate('/');
      return;
    }

    try {
      const orderData = await ordersAPI.getById(parseInt(orderId));

      if (orderData.status !== 'mutfak' && orderData.status !== 'hazır') {
        toast.error('Bu sipariş zaten ödenmiş veya iptal edilmiş');
        navigate('/');
        return;
      }

      setOrder(orderData);
    } catch (error) {
      toast.error('Sipariş bulunamadı');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Hediye kodu fonksiyonları
  const validateGiftCode = async () => {
    if (!giftCode.trim() || !order) {
      toast.error('Lütfen bir hediye kodu girin');
      return;
    }

    setIsValidatingCode(true);
    try {
      const cartItems = order.items?.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price
      })) || [];

      const validation = await giftCodesAPI.validate(giftCode.trim(), order.total_amount, cartItems);

      if (validation.valid && validation.giftCode) {
        // İndirim miktarını hesapla
        let discountAmount = 0;
        if (validation.giftCode.discount_type === 'percentage') {
          discountAmount = (order.total_amount * validation.giftCode.discount_value) / 100;
        } else {
          discountAmount = validation.giftCode.discount_value;
        }

        setAppliedGiftCode(validation.giftCode);
        setGiftCodeDiscount(discountAmount);
        toast.success(`✅ ${validation.message}`);
      }
    } catch (error: any) {
      // console.error('❌ Hediye kodu doğrulama hatası:', error);
      toast.error(error.response?.data?.error || 'Kod doğrulanamadı');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
    } finally {
      setIsValidatingCode(false);
    }
  };

  const removeGiftCode = () => {
    setGiftCode('');
    setAppliedGiftCode(null);
    setGiftCodeDiscount(0);
    toast.success('Hediye kodu kaldırıldı');
  };

  const calculateFinalTotal = () => {
    if (!order) return 0;
    return Math.max(0, order.total_amount - giftCodeDiscount);
  };

  const calculateChange = () => {
    const finalTotal = calculateFinalTotal();
    const paid = parseFloat(customerPaid) || 0;
    return Math.max(0, paid - finalTotal);
  };

  // SumUp ödeme durumu kontrol et (sadece DB'den - webhook tabanlı, polling yok)
  const checkSumUpPaymentStatus = async (isManual = false) => {
    if (!order || !sumupCheckoutId) return;

    try {
      if (isManual) {
        toast.loading('Durum kontrol ediliyor...', { id: 'check-status' });
      }

      const status = await ordersAPI.getSumUpStatus(
        order.id,
        appliedGiftCode?.id,
        giftCodeDiscount
      );

      if (isManual) {
        toast.dismiss('check-status');
      }

      if (status.completed) {
        // Ödeme tamamlandı
        setIsWaitingForCardPayment(false);
        setSumupCheckoutId(null);
        toast.dismiss('card-payment-waiting');

        if (appliedGiftCode && giftCodeDiscount > 0) {
          toast.success(`🎁 Hediye kodu kullanıldı: -${formatPrice(giftCodeDiscount)}`);
        }

        toast.success('💳 Kart ödemesi başarıyla tamamlandı!');

        // Fiş yazdırma sayfasına yönlendir
        navigate(`/receipt/${order.id}`, {
          state: {
            paymentType: 'kart',
            completedAt: new Date().toISOString()
          }
        });
      } else if (status.status === 'FAILED' || status.status === 'CANCELLED' || status.status === 'EXPIRED') {
        // Ödeme başarısız
        setIsWaitingForCardPayment(false);
        setSumupCheckoutId(null);
        toast.dismiss('card-payment-waiting');
        toast.error('Kart ödemesi başarısız oldu. Lütfen tekrar deneyin.');
      } else {
        // PENDING/PROCESSING
        if (isManual) {
          toast('Ödeme henüz tamamlanmadı, lütfen POS cihazını kontrol edin.', { icon: '⏳' });
        }
      }
    } catch (error: any) {
      if (isManual) toast.dismiss('check-status');
      console.error('SumUp ödeme durumu kontrol hatası:', error);
      if (isManual) toast.error('Durum kontrol edilemedi');
    }
  };

  // SumUp ödeme durumu kontrolü - webhook tabanlı + polling fallback
  // Polling tekrar eklendi - Backend'den DB durumu sorgulanır (SumUp API değil)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isWaitingForCardPayment && sumupCheckoutId && order) {
      // İlk kontrol hemen yap
      checkSumUpPaymentStatus(false);

      // Her 3 saniyede bir kontrol et (Backend DB'den bakar, heavy load yaratmaz)
      interval = setInterval(() => {
        checkSumUpPaymentStatus(false);
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isWaitingForCardPayment, sumupCheckoutId, order]);

  // Sayfa focus olduğunda kontrol et (kullanıcı etkileşimi)
  useEffect(() => {
    const handleFocus = () => {
      if (isWaitingForCardPayment && order) {
        checkSumUpPaymentStatus(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isWaitingForCardPayment, order]);

  const processPayment = async () => {
    if (!order) return;

    // KART ödemesi için özel durum: Backend response'unu beklerken buton durumunu güncelle
    if (paymentType === 'kart') {
      // Buton durumunu hemen güncelle - "POS Ödeme bekleniyor..." göster
      // Bu state değişikliği butonun metnini değiştirecek
      setIsWaitingForPosResponse(true);
      setProcessing(true);
      // Test için: State güncellendi mi kontrol et
      setTimeout(() => {
        console.log('🔍 State kontrolü - isWaitingForPosResponse:', isWaitingForPosResponse);
      }, 100);
    } else {
      setProcessing(true);
    }

    try {
      // Ödemeyi al - hediye kodu bilgilerini backend'e gönder
      // AWAIT ile backend response'unu BEKLE - response gelene kadar burada bekler
      const response = await ordersAPI.pay(
        order.id,
        paymentType,
        appliedGiftCode?.id,
        giftCodeDiscount
      );

      // KART ÖDEME: SumUp aktifse POS bekleme moduna geç, değilse normal akışa devam et
      if (paymentType === 'kart') {
        // Backend response'unu kontrol et
        const hasCheckoutId = response && typeof response === 'object' && 'checkoutId' in response && response.checkoutId;
        const hasSuccess = response && typeof response === 'object' && 'success' in response && response.success === true;
        const hasMessage = response && typeof response === 'object' && 'message' in response; // SumUp pasifken döner

        // SumUp AKTİF: checkoutId veya success varsa POS bekleme moduna geç
        if (hasCheckoutId || hasSuccess) {
          // Backend'den başarılı response geldi
          // Artık bekleme moduna geç - buton durumunu güncelle
          setIsWaitingForPosResponse(false);
          setProcessing(false);
          setIsWaitingForCardPayment(true);
          setSumupCheckoutId(response.checkoutId || null);

          // Cloud API'de ödeme direkt POS cihazında başlar
          toast.loading('💳 POS cihazında ödeme başlatıldı. Lütfen kartı okutun...', {
            duration: 10000,
            id: 'card-payment-waiting'
          });

          // Polling başlayacak (useEffect ile) - Webhook ile takip edilir
          return;
        }
        // SumUp PASİF: message varsa ödeme tamamlandı demektir, normal akışa devam et
        else if (hasMessage) {
          console.log('✅ Kart ödemesi tamamlandı (SumUp pasif):', response.message);
          // Normal akışa devam et (aşağıdaki fatura sayfasına yönlendirme kullanılacak)
        }
        // Hiçbiri yoksa hata
        else {
          toast.error('Ödeme işlenemedi. Lütfen tekrar deneyin.');
          setIsWaitingForPosResponse(false);
          setProcessing(false);
          return;
        }
      }

      // NAKİT ödeme için mevcut akış
      if (appliedGiftCode && giftCodeDiscount > 0) {
        toast.success(`🎁 Hediye kodu kullanıldı: -${formatPrice(giftCodeDiscount)}`);
      }

      toast.success('Ödeme başarıyla tamamlandı! Mutfağa iletildi.');

      // Fiş yazdırma sayfasına yönlendir
      navigate(`/receipt/${order.id}`, {
        state: {
          paymentType,
          completedAt: new Date().toISOString()
        }
      });

    } catch (error: any) {
      console.error('❌ Ödeme işlemi hatası:', error);

      // Daha detaylı hata mesajı
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Ödeme işlemi başarısız';

      // SumUp hatası ise özel mesaj göster
      if (errorMessage.includes('SumUp') || errorMessage.includes('sumup')) {
        toast.error(`💳 Kart ödeme hatası: ${errorMessage}. Lütfen nakit ödeme seçeneğini deneyin.`);
      } else {
        toast.error(`Ödeme işlemi başarısız: ${errorMessage}`);
      }

      // Hata durumunda siparişi yeniden yükle (durum değişmiş olabilir)
      if (order) {
        try {
          const updatedOrder = await ordersAPI.getById(order.id);
          setOrder(updatedOrder);
        } catch (reloadError) {
          console.error('Sipariş yeniden yüklenemedi:', reloadError);
        }
      }
    } finally {
      setIsWaitingForPosResponse(false);
      setProcessing(false);
    }
  };

  const cancelPayment = () => {
    if (order?.table_id) {
      navigate(`/order/${order.table_id}/${order.id}`);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cafe-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown mx-auto mb-4"></div>
          <span className="text-cafe-brown text-lg">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-cafe-cream flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sipariş bulunamadı</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-cafe-brown text-white px-6 py-3 rounded-lg hover:bg-cafe-dark transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  // Ödeme izni kontrolü
  if (!user?.can_process_payments && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-cafe-cream flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-cafe-brown mb-2">Ödeme İzniniz Yok</h2>
          <p className="text-gray-600 mb-4">Bu işlem için ödeme alma yetkiniz bulunmuyor.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-cafe-brown text-white px-6 py-3 rounded-lg hover:bg-cafe-dark transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cafe-cream">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-cafe-brown mb-2">💳 Ödeme</h1>
            <p className="text-gray-600">
              {!order.table ? '🚀 Hızlı Sipariş' : `Masa ${order.table.table_number}`} - Sipariş #{order.id}
            </p>
          </div>

          {/* Sipariş Özeti */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-3">📋 Sipariş Özeti</h3>

            <div className="space-y-3 mb-4">
              {order.items?.map((item) => (
                <div key={item.id} className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{item.product?.name}</div>
                    {item.added_by_user && (
                      <div className="text-blue-600 text-sm font-medium">
                        👤 {item.added_by_user.username}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">{formatPrice(item.unit_price)} x {item.quantity}</div>
                    <div className="font-semibold text-gray-800">{formatPrice(item.unit_price * item.quantity)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              {/* Vergi detayları */}
              {(() => {
                const subtotal = order.items?.reduce((total, item) =>
                  total + (item.unit_price * item.quantity), 0) || 0;

                // KDV hesaplama - Sadece bir tür kullanılır
                const globalTaxRate = parseFloat(localStorage.getItem('globalTaxRate') || '0');
                const isGlobalTaxEnabled = JSON.parse(localStorage.getItem('isGlobalTaxEnabled') || 'false');

                let taxTotal = 0;
                let taxInfo = '';

                if (isGlobalTaxEnabled && globalTaxRate > 0) {
                  // Global KDV aktifse, sadece onu kullan
                  taxTotal = subtotal * (globalTaxRate / 100);
                  taxInfo = `KDV (%${globalTaxRate})`;
                } else {
                  // Global KDV aktif değilse, ürün bazlı KDV kullan
                  const uniqueTaxRates = new Set(order.items?.map(item => item.product?.tax_rate || 0).filter(rate => rate > 0));

                  // Vergi hesaplama (Fiyata Dahil Mod - Belçika)
                  if (uniqueTaxRates.size === 1) {
                    const rate = Array.from(uniqueTaxRates)[0];
                    taxTotal = order.items?.reduce((total, item) => {
                      const itemTotal = item.unit_price * item.quantity;
                      const taxAmount = itemTotal - (itemTotal / (1 + rate / 100));
                      return total + taxAmount;
                    }, 0) || 0;
                    taxInfo = `KDV (%${rate})`;
                  } else if (uniqueTaxRates.size > 1) {
                    taxTotal = order.items?.reduce((total, item) => {
                      const itemTotal = item.unit_price * item.quantity;
                      const taxRate = item.product?.tax_rate || 0;
                      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
                      return total + taxAmount;
                    }, 0) || 0;
                    taxInfo = 'KDV (Farklı oranlar)';
                  } else {
                    taxInfo = 'KDV yok';
                  }
                }

                // Kuver ücretini hesapla
                const coverCharge = parseFloat(localStorage.getItem('coverCharge') || '0');
                const isCoverChargeEnabled = JSON.parse(localStorage.getItem('isCoverChargeEnabled') || 'false');
                const tableCapacity = order.table?.capacity || 4;
                const totalCoverCharge = isCoverChargeEnabled && coverCharge > 0 ? coverCharge * tableCapacity : 0;

                // Grand total (Fiyata Dahil Mod - toplam zaten vergili)
                const finalTotal = subtotal + totalCoverCharge;

                return (
                  <>
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Ara Toplam:</span>
                        <span className="font-medium">{formatPrice(subtotal)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-gray-600">Vergi:</span>
                          <div className="text-xs text-gray-500">{taxInfo}</div>
                        </div>
                        <span className="font-medium">{formatPrice(taxTotal)}</span>
                      </div>
                      {totalCoverCharge > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Kuber ({tableCapacity} kişi):</span>
                          <span className="font-medium">{formatPrice(totalCoverCharge)}</span>
                        </div>
                      )}
                    </div>
                    {giftCodeDiscount > 0 && (
                      <div className="flex justify-between items-center text-green-600">
                        <div>
                          <span>🎁 Hediye Kodu İndirimi:</span>
                          <div className="text-sm text-gray-500">{appliedGiftCode?.code}</div>
                        </div>
                        <span className="font-medium">-{formatPrice(giftCodeDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-2xl font-bold border-t pt-3">
                      <span className="text-gray-800">Genel Toplam:</span>
                      <span className="text-cafe-brown">{formatPrice(calculateFinalTotal())}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Hediye Kodu Bölümü */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">🎁 Hediye Kodu</h3>

            {!appliedGiftCode ? (
              <div className="flex gap-3">
                <input
                  type="text"
                  value={giftCode}
                  onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                  placeholder="Hediye kodu girin..."
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cafe-brown focus:border-transparent"
                />
                <button
                  onClick={validateGiftCode}
                  disabled={!giftCode.trim() || isValidatingCode}
                  className="bg-cafe-brown text-white px-6 py-3 rounded-lg hover:bg-cafe-dark transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isValidatingCode ? '⏳ Kontrol Ediliyor...' : '✅ Uygula'}
                </button>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-green-700 font-semibold">
                      ✅ {appliedGiftCode.code} uygulandı
                    </span>
                    <div className="text-sm text-green-600">
                      {formatPrice(giftCodeDiscount)} indirim
                    </div>
                  </div>
                  <button
                    onClick={removeGiftCode}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                  >
                    ❌ Kaldır
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Ödeme Türü Seçimi */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">💳 Ödeme Yöntemi</h3>

            <div className="space-y-3">
              <button
                onClick={() => setPaymentType('nakit')}
                className={`w-full p-4 rounded-lg border-2 transition-all duration-300 ${paymentType === 'nakit'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-green-300'
                  }`}
              >
                <div className="flex items-center justify-center">
                  <span className="text-2xl mr-3">💵</span>
                  <div>
                    <div className="font-semibold">Nakit</div>
                    <div className="text-sm opacity-75">Nakit ödeme</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setPaymentType('kart')}
                className={`w-full p-4 rounded-lg border-2 transition-all duration-300 ${paymentType === 'kart'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                  }`}
              >
                <div className="flex items-center justify-center">
                  <span className="text-2xl mr-3">💳</span>
                  <div>
                    <div className="font-semibold">SumUp Kart</div>
                    <div className="text-sm opacity-75">SumUp POS ile kart ödemesi</div>
                  </div>
                </div>
              </button>
            </div>

            {/* Para Üstü Hesaplama - Sadece Nakit için */}
            {paymentType === 'nakit' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Müşteri Verdi:
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={customerPaid}
                    onChange={(e) => setCustomerPaid(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                  <span className="text-sm font-medium text-gray-500">€</span>
                  {customerPaid && parseFloat(customerPaid) > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <span className="text-sm font-medium text-green-700">Para Üstü:</span>
                      <span className="text-sm font-bold text-green-800">{formatPrice(calculateChange())}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* İşlem Butonları */}
          <div className="flex space-x-3">
            <button
              onClick={cancelPayment}
              disabled={processing}
              className="flex-1 bg-gray-500 text-white py-4 px-6 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 font-semibold"
            >
              ❌ İptal
            </button>
            <button
              onClick={processPayment}
              disabled={processing || isWaitingForPosResponse}
              className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white py-4 px-6 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {isWaitingForPosResponse ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  POS Ödeme bekleniyor...
                </div>
              ) : processing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  İşleniyor...
                </div>
              ) : (
                `✅ ${formatPrice(calculateFinalTotal())} Öde`
              )}
            </button>
          </div>

          {/* Bilgi Notu */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💡 Bilgi:</strong> Ödeme tamamlandıktan sonra masa otomatik olarak boş duruma geçecektir.
            </p>
          </div>
        </div>
      </div>

      {/* Kart Ödeme Bekleme Ekranı */}
      {isWaitingForCardPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">💳 Kart Ödemesi Bekleniyor</h2>
              <p className="text-gray-600 mb-6">
                POS cihazında ödemeyi tamamlayın. Ödeme tamamlandığında otomatik kapanacaktır.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>💡 Not:</strong> Ödeme POS cihazında başlatıldı. Lütfen kartı POS cihazına okutun veya takın.
                </p>
              </div>

              <button
                onClick={() => checkSumUpPaymentStatus(true)}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors mb-3 font-semibold"
              >
                🔄 Durumu Kontrol Et
              </button>

              <button
                onClick={() => {
                  setIsWaitingForCardPayment(false);
                  setSumupCheckoutId(null);
                  toast.dismiss('card-payment-waiting');
                }}
                className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
              >
                İptal Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;
